const SHORT_SCOPE_LABELS = {
  recoveryAssistEnabled: 'Recovery',
  lossInsuranceEnabled: 'Insurance',
  closeFollowEnabled: 'Close follow',
}

const OPERATIONAL_REASON_LABELS = {
  NONE: 'OK',
  RECOVERY_OFF: 'REC-OFF',
  INSURANCE_OFF: 'INS-OFF',
  CLOSE_FOLLOW_OFF: 'CF-OFF',
  RECOVERY_SUGGEST_ONLY: 'REC-SUG',
  CLOSE_FOLLOW_APPROVAL_REQUIRED: 'CF-APPROVE',
  CLOSE_FOLLOW_PAUSES_RECOVERY: 'CF-HOLD',
  COOLDOWN_ACTIVE: 'COOLDOWN',
  OVERLAP_BLOCKED: 'OVERLAP',
  INSURANCE_BUFFER_WAIT: 'INS-WAIT',
}

const DECISION_KIND_LABELS = {
  OPEN: 'OPEN',
  WAIT: 'WAIT',
  SKIP: 'SKIP',
  BLOCK: 'BLOCK',
}

function toNumber(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function isOn(value) {
  return value === true
}

const REASON_TEXT = {
  NONE: 'İşlem devam eder.',
  RECOVERY_OFF: 'Recovery kapalı.',
  INSURANCE_OFF: 'Insurance kapalı.',
  CLOSE_FOLLOW_OFF: 'Close-follow kapalı.',
  RECOVERY_SUGGEST_ONLY: 'Recovery sadece öneri üretir.',
  CLOSE_FOLLOW_APPROVAL_REQUIRED: 'Close-follow işlem öncesi onay ister.',
  CLOSE_FOLLOW_PAUSES_RECOVERY: 'Close-follow recovery baskısını durdurur.',
  COOLDOWN_ACTIVE: 'Cooldown aktif.',
  OVERLAP_BLOCKED: 'Aynı pozisyonda çakışma engellendi.',
  INSURANCE_BUFFER_WAIT: 'Insurance tamponu dolmadan yeni recovery açılmaz.',
}

function reason(code, detail = null) {
  return {
    code,
    label: OPERATIONAL_REASON_LABELS[code] || code,
    text: REASON_TEXT[code] || code,
    detail,
  }
}

function decision(kind, reasonCode, detail = null) {
  return {
    kind,
    kindLabel: DECISION_KIND_LABELS[kind] || kind,
    ...reason(reasonCode, detail),
  }
}

function decisionSummary(decisionValue, axisReasons = [], conflictReasons = []) {
  const kind = decisionValue?.kind || 'OPEN'
  const label = decisionValue?.label || 'OK'
  const text = decisionValue?.text || 'İşlem devam eder.'
  const activeAxes = (axisReasons || [])
    .filter(item => item && item.code !== 'NONE')
    .map(item => item.label)
    .filter(Boolean)
  const blocker = (conflictReasons || [])[0]
  const blockerText = blocker ? ` Etki: ${blocker.label}.` : ''
  const axisText = activeAxes.length ? ` Aktif: ${activeAxes.join(' · ')}.` : ''
  if (kind === 'OPEN') return `Açık: ${text}${axisText}`
  if (kind === 'WAIT') return `Beklemede: ${label}. ${text}${axisText}${blockerText}`
  if (kind === 'SKIP') return `Atlandı: ${label}. ${text}${axisText}${blockerText}`
  if (kind === 'BLOCK') return `Bloklandı: ${label}. ${text}${axisText}${blockerText}`
  return `${kind}: ${label}. ${text}`
}

export function strategySafetyGovernorCoordinationSummary(config = {}) {
  const recoveryEnabled = isOn(config.recoveryAssistEnabled)
  const recoveryMode = config.recoveryAssistMode === 'AUTO_OPEN' ? 'AUTO_OPEN' : 'SUGGEST_ONLY'
  const insuranceEnabled = isOn(config.lossInsuranceEnabled)
  const closeFollowEnabled = isOn(config.closeFollowEnabled)
  const closeFollowApproval = isOn(config.closeFollowRequireApproval)
  const pauseRecovery = config.closeFollowPauseRecovery !== false

  const activeCount = [recoveryEnabled, insuranceEnabled, closeFollowEnabled].filter(Boolean).length
  const scopeTags = [
    recoveryEnabled ? (recoveryMode === 'AUTO_OPEN' ? 'Recovery auto' : 'Recovery öneri') : null,
    insuranceEnabled ? 'Insurance açık' : null,
    closeFollowEnabled ? (closeFollowApproval ? 'Close follow onaylı' : 'Close follow açık') : null,
  ].filter(Boolean)

  const lines = []
  if (activeCount === 0) {
    lines.push('Guardrail kapalı.')
  } else {
    lines.push(`${activeCount} guardrail aktif: ${scopeTags.join(' · ')}.`)
  }

  if (recoveryEnabled) {
    lines.push(`Recovery ${recoveryMode === 'AUTO_OPEN' ? 'auto-open' : 'suggest-only'}.`)
    lines.push(`Cooldown ${toNumber(config.recoveryCooldownMinutes, 30)} dk · risk %${toNumber(config.recoveryMaxRiskPct, 25)}.`)
  }

  if (insuranceEnabled) {
    lines.push(`Insurance prim %${toNumber(config.insurancePremiumPercent, 20)} · açık hasar ${toNumber(config.insuranceMaxOpenDamageUsdt, 100)} USDT.`)
  }

  if (closeFollowEnabled) {
    lines.push(`Close-follow ${closeFollowApproval ? 'onaylı' : 'free'}.`)
    lines.push(`Recovery ${pauseRecovery ? 'hold' : 'run'} · basket ${toNumber(config.closeFollowMinNetProfitUsdt, 5)} USDT · max ${toNumber(config.closeFollowMaxClosesPerCycle, 3)}.`)
  }

  return lines
}

export function strategySafetyGovernorDecisionReason(config = {}) {
  const recoveryEnabled = isOn(config.recoveryAssistEnabled)
  const insuranceEnabled = isOn(config.lossInsuranceEnabled)
  const closeFollowEnabled = isOn(config.closeFollowEnabled)

  if (!recoveryEnabled && !insuranceEnabled && !closeFollowEnabled) {
    return decision('OPEN', 'NONE', 'Guardrail kapalı.')
  }
  if (closeFollowEnabled && isOn(config.closeFollowRequireApproval)) {
    return decision('WAIT', 'CLOSE_FOLLOW_APPROVAL_REQUIRED', 'İşlem öncesi onay gerekir.')
  }
  if (closeFollowEnabled && config.closeFollowPauseRecovery !== false && recoveryEnabled) {
    return decision('BLOCK', 'CLOSE_FOLLOW_PAUSES_RECOVERY', 'Close-follow recovery’yi bekletir.')
  }
  if (recoveryEnabled && insuranceEnabled && Number(config.insuranceReserveTargetPercent || 0) > 0) {
    return decision('WAIT', 'INSURANCE_BUFFER_WAIT', `Insurance tamponu %${toNumber(config.insuranceReserveTargetPercent, 8)}.`)
  }
  if (recoveryEnabled && Number(config.recoveryCooldownMinutes || 0) > 0) {
    return decision('WAIT', 'COOLDOWN_ACTIVE', `${toNumber(config.recoveryCooldownMinutes, 30)} dk cooldown.`)
  }
  if (recoveryEnabled && config.recoveryAssistMode !== 'AUTO_OPEN') {
    return decision('SKIP', 'RECOVERY_SUGGEST_ONLY', 'Recovery açılmaz; yalnız öneri üretilir.')
  }
  return decision('OPEN', 'NONE', 'Guardrail koordinasyonu izin veriyor.')
}

export function strategySafetyGovernorConflictSummary(config = {}) {
  const items = []
  const seen = new Set()
  const push = (code, detail) => {
    if (seen.has(code)) return
    seen.add(code)
    items.push(reason(code, detail))
  }
  if (isOn(config.recoveryAssistEnabled) && isOn(config.closeFollowEnabled) && config.closeFollowPauseRecovery !== false) {
    push('OVERLAP_BLOCKED', 'Recovery ve close-follow aynı anda kilitlenir.')
  }
  if (isOn(config.lossInsuranceEnabled) && isOn(config.recoveryAssistEnabled) && Number(config.insuranceReserveTargetPercent || 0) > 0) {
    push('INSURANCE_BUFFER_WAIT', 'Insurance tamponu recovery’yi bekletir.')
  }
  if (isOn(config.closeFollowEnabled) && isOn(config.closeFollowRequireApproval)) {
    push('CLOSE_FOLLOW_APPROVAL_REQUIRED', 'Close-follow onay bekler.')
  }
  return items
}

export function strategySafetyGovernorAxisSummary(config = {}) {
  const recoveryEnabled = isOn(config.recoveryAssistEnabled)
  const insuranceEnabled = isOn(config.lossInsuranceEnabled)
  const closeFollowEnabled = isOn(config.closeFollowEnabled)

  return [
    recoveryEnabled
      ? (config.recoveryAssistMode === 'AUTO_OPEN'
        ? reason('NONE', 'Recovery açık ve auto-open.')
        : reason('RECOVERY_SUGGEST_ONLY', 'Recovery sadece öneri üretir.'))
      : reason('RECOVERY_OFF', 'Recovery kapalı.'),
    insuranceEnabled
      ? reason('NONE', 'Insurance açık.')
      : reason('INSURANCE_OFF', 'Insurance kapalı.'),
    closeFollowEnabled
      ? (isOn(config.closeFollowRequireApproval)
        ? reason('CLOSE_FOLLOW_APPROVAL_REQUIRED', 'Close-follow onay bekler.')
        : reason('NONE', 'Close-follow açık.'))
      : reason('CLOSE_FOLLOW_OFF', 'Close-follow kapalı.'),
  ]
}

export function strategySafetyGovernorCoordinationState(config = {}) {
  const recoveryEnabled = isOn(config.recoveryAssistEnabled)
  const insuranceEnabled = isOn(config.lossInsuranceEnabled)
  const closeFollowEnabled = isOn(config.closeFollowEnabled)
  const activeCount = [recoveryEnabled, insuranceEnabled, closeFollowEnabled].filter(Boolean).length
  const approvalRequired = closeFollowEnabled && isOn(config.closeFollowRequireApproval)
  const pauseRecovery = closeFollowEnabled && config.closeFollowPauseRecovery !== false
  const conflictReasons = strategySafetyGovernorConflictSummary(config)
  const axisReasons = strategySafetyGovernorAxisSummary(config)
  const decisionValue = strategySafetyGovernorDecisionReason(config)
  return {
    activeCount,
    recoveryEnabled,
    recoveryMode: config.recoveryAssistMode === 'AUTO_OPEN' ? 'AUTO_OPEN' : 'SUGGEST_ONLY',
    insuranceEnabled,
    closeFollowEnabled,
    closeFollowApproval: approvalRequired,
    pauseRecovery,
    scopeLabels: [
      recoveryEnabled ? SHORT_SCOPE_LABELS.recoveryAssistEnabled : null,
      insuranceEnabled ? SHORT_SCOPE_LABELS.lossInsuranceEnabled : null,
      closeFollowEnabled ? SHORT_SCOPE_LABELS.closeFollowEnabled : null,
    ].filter(Boolean),
    decisionReason: decisionValue,
    axisReasons,
    conflictReasons,
    decisionSummary: decisionSummary(decisionValue, axisReasons, conflictReasons),
  }
}
